import { useEffect, useState } from 'react';
import type { Article } from '../../../../api/_lib/lovinMalta';
import { formatRelativeTime } from '../../../utils/widgetFormatters';
import ArticleTile from './ArticleTile';
import FeatureSection from '../../FeatureSection';
import KBYVCarousel from '../../KnowBeforeYouVote/KBYVCarousel';
import Carousel from '../../Carousel/Carousel';
import './LatestNews.css';

type Status = 'loading' | 'ready' | 'empty' | 'error';

interface State {
  status: Status;
  articles: Article[];
  fetchedAt: string | null;
}

const SKELETON_COUNT = 6;

interface Props {
  /** Layout:
   *   · `rail`     — vertical stack (default; right rail on Normal)
   *   · `row`      — responsive 3-up grid (no scroll)
   *   · `carousel` — horizontal scroll-snap track with prev/next
   *                  buttons + "more →" CTA (legacy band layout)
   *   · `grid-2x4` — 4 columns × 2 rows = 8 visible cards (the
   *                  polls_open page uses this; rows collapse to
   *                  fewer columns on tablet/mobile).
   *  Card variant (image on top) is used by `row`, `carousel`, and
   *  `grid-2x4`. `rail` keeps the compact 80×80-thumb tile. */
  layout?: 'rail' | 'row' | 'carousel' | 'grid-2x4';
  /** Show the kicker + title + updated-time header. Default true.
   *  The polls_open band passes false so the row reads as just
   *  the cards (the band's hero already frames it). */
  showHeader?: boolean;
}

export default function LatestNewsTiles({
  layout = 'rail',
  showHeader = true,
}: Props = {}) {
  const [state, setState] = useState<State>({
    status: 'loading',
    articles: [],
    fetchedAt: null,
  });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    fetch('/api/news/latest', { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<{ articles: Article[]; fetchedAt: string }>;
      })
      .then((data) => {
        if (cancelled) return;
        setState({
          status: data.articles.length === 0 ? 'empty' : 'ready',
          articles: data.articles,
          fetchedAt: data.fetchedAt,
        });
      })
      .catch((err) => {
        if (cancelled || err?.name === 'AbortError') return;
        console.error('[LatestNewsTiles] fetch failed:', err);
        setState((prev) => ({ ...prev, status: 'error' }));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const rootClass =
    layout === 'rail'
      ? 'latest-news'
      : 'latest-news latest-news--row'; // every non-rail layout drops
                                        // the rail chrome (no border,
                                        // no background, no padding)
  const listClass =
    layout === 'row'
      ? 'latest-news__list latest-news__list--row'
      : layout === 'grid-2x4'
        ? 'latest-news__list latest-news__list--grid-2x4'
        : 'latest-news__list';

  // Carousel + row both use the bigger image-led card variant;
  // rail keeps the compact 80×80-thumb tile.
  const tileVariant = layout === 'rail' ? 'compact' : 'card';

  return (
    <aside className={rootClass} aria-label="Latest from Lovin Malta">
      {showHeader && (
        <header className="latest-news__header">
          <p className="latest-news__kicker">Latest from</p>
          <h2 className="latest-news__title">Lovin Malta</h2>
          {state.fetchedAt && (
            <span className="latest-news__updated">
              Updated {formatRelativeTime(state.fetchedAt)}
            </span>
          )}
        </header>
      )}

      {state.status === 'loading' && <SkeletonList layout={layout} />}

      {state.status === 'ready' && layout === 'carousel' && (
        <Carousel
          // Show exactly 4 cards across the full band width.
          // 4 cards + 3 × 20 px gap = 100% → card_w = (100% - 60px) / 4.
          // The calc adapts as the band's max-width responds to
          // viewport; on the polls_open page the band is 1500 px so
          // each card is ~344 px. The carousel scrolls horizontally
          // for the 5th+ article.
          itemMin="calc((100% - 60px) / 4)"
          gap="20px"
          ariaLabel="Latest articles from Lovin Malta"
          moreLink={{
            href: 'https://lovinmalta.com/news/general-election-2026/',
            label: 'More on Lovin Malta →',
          }}
        >
          {state.articles.map((a) => (
            <ArticleTile key={a.id} article={a} variant={tileVariant} />
          ))}
        </Carousel>
      )}

      {state.status === 'ready' && layout !== 'carousel' && (
        <ul className={listClass}>
          {(layout === 'grid-2x4'
            ? state.articles.slice(0, 8)
            : state.articles
          ).map((a) => (
            <li key={a.id}>
              <ArticleTile article={a} variant={tileVariant} />
            </li>
          ))}
        </ul>
      )}

      {state.status === 'empty' && (
        <p className="latest-news__empty">
          No articles tagged &lsquo;general election 2026&rsquo; yet.
        </p>
      )}

      {state.status === 'error' && (
        <p className="latest-news__error">
          Couldn&apos;t load latest articles. Try refreshing the page.
        </p>
      )}

      {/* Know Before You Vote candidate carousel — sponsored section that
          sits below the news tiles in the same rail. Feature-flagged so
          it can be hidden without touching the news rail above it. */}
      <FeatureSection sectionKey="featured_candidate" label="Know Before You Vote">
        <KBYVCarousel />
      </FeatureSection>
    </aside>
  );
}

function SkeletonList({
  layout = 'rail',
}: { layout?: 'rail' | 'row' | 'carousel' | 'grid-2x4' }) {
  // All non-rail layouts share the row grid for the skeleton state —
  // any carousel / grid chrome only appears once data lands.
  const listClass =
    layout === 'rail'
      ? 'latest-news__list'
      : layout === 'grid-2x4'
        ? 'latest-news__list latest-news__list--grid-2x4'
        : 'latest-news__list latest-news__list--row';
  return (
    <ul className={listClass} aria-hidden="true">
      {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
        <li key={i}>
          <div className="article-tile article-tile--skeleton">
            <div className="article-tile__thumb article-tile__thumb--skeleton" />
            <div className="article-tile__body">
              <span className="skeleton-line skeleton-line--short" />
              <span className="skeleton-line" />
              <span className="skeleton-line skeleton-line--short" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
